// Gedrag van de ClaimSafe-reviews-sectie (sections/ab-product-reviews),
// geport van het Elvou-theme: sterrenfilters ("Alle" = ≥3★), laad-meer,
// masonry-layout (absolute positionering per kolom) en een scrollvenster met
// fade-gradient. De reviews zelf zijn server-side gerenderd uit de
// product-metafields; de sync aggregeert daar alle varianten en legacy-SKU's,
// dus dit component hoeft niets met variantwissels.
const CARD_HIDDEN_CLASS = "ab-review-card--hidden";
const FILTER_ACTIVE_CLASS = "ab-reviews__filter--active";
const MASONRY_GAP = 16;

class AbClaimsafeReviews extends HTMLElement {
  #abortController = null;
  #cards = [];
  #filteredCards = [];
  #activeFilter = "all";
  #visibleCount = 0;
  #currentPageSize = 0;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Chronologisch (nieuwste eerst) op echte timestamp — onafhankelijk van
    // de metafield-volgorde.
    this.#cards = Array.from(this.querySelectorAll("[data-review-card]"));
    this.#cards.sort((a, b) => (Number(b.dataset.createdTs) || 0) - (Number(a.dataset.createdTs) || 0));

    const scroll = this.#el("[data-reviews-scroll]");
    scroll?.addEventListener("scroll", () => this.#updateGradient(), { passive: true, signal });

    this.#el("[data-load-more]")?.addEventListener(
      "click",
      () => {
        this.#visibleCount += this.#pageSize();
        this.#renderVisibleCards();
        this.#updateLoadMoreVisibility();
        requestAnimationFrame(() => this.#updateGradient());
      },
      { signal },
    );

    for (const btn of this.querySelectorAll("[data-filter]")) {
      btn.addEventListener(
        "click",
        () => {
          for (const b of this.querySelectorAll("[data-filter]")) {
            b.classList.toggle(FILTER_ACTIVE_CLASS, b === btn);
            b.setAttribute("aria-pressed", b === btn ? "true" : "false");
          }
          this.#applyFilter(btn.dataset.filter);
        },
        { signal },
      );
    }

    this.#currentPageSize = this.#pageSize();
    window.addEventListener(
      "resize",
      () => {
        this.#updateScrollMaxHeight();
        const newPageSize = this.#pageSize();
        if (newPageSize !== this.#currentPageSize) {
          this.#currentPageSize = newPageSize;
          this.#visibleCount = Math.max(this.#visibleCount, newPageSize);
          this.#renderVisibleCards();
          this.#updateLoadMoreVisibility();
        }
        requestAnimationFrame(() => this.#updateMasonryLayout());
        requestAnimationFrame(() => this.#updateGradient());
      },
      { signal },
    );

    // Masonry herberekenen zodra reviewafbeeldingen (sterren) laden
    for (const image of this.querySelectorAll("[data-review-card] img")) {
      if (image.complete) continue;
      image.addEventListener("load", () => requestAnimationFrame(() => this.#updateMasonryLayout()), {
        once: true,
        signal,
      });
    }

    this.#updateScrollMaxHeight();
    this.#visibleCount = this.#pageSize();
    this.#applyFilter("all");
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #el(selector) {
    return this.querySelector(selector);
  }

  #pageSize() {
    const mobile = Number(this.dataset.initialMobile) || 12;
    const desktop = Number(this.dataset.initialDesktop) || 24;
    return window.matchMedia("(min-width: 768px)").matches ? desktop : mobile;
  }

  #columnCount() {
    if (window.matchMedia("(min-width: 1024px)").matches) return 3;
    if (window.matchMedia("(min-width: 768px)").matches) return 2;
    return 1;
  }

  #applyFilter(filter) {
    this.#activeFilter = filter;
    this.#visibleCount = this.#pageSize();
    this.#filteredCards = this.#cards.filter((card) => this.#matchesFilter(card));

    this.#renderVisibleCards();
    this.#updateLoadMoreVisibility();

    const count = this.#filteredCards.length;
    const foundEl = this.#el("[data-filter-count]");
    if (foundEl) {
      const t = (key) => this.querySelector(`[data-t="${key}"]`)?.textContent.trim() || "";
      foundEl.textContent =
        count === 1 ? t("found-one") : (t("found-other") || "__COUNT__").replace("__COUNT__", String(count));
      // Bij "Alle" geen aantal tonen: dat filter toont ≥3★ en spreekt daarmee het
      // totaal in de ratingheader tegen. Alleen bij een expliciet sterfilter.
      foundEl.toggleAttribute("hidden", count === 0 || filter === "all");
    }
    this.#el("[data-empty-state]")?.toggleAttribute("hidden", count > 0);
    this.#el("[data-reviews-wrapper]")?.toggleAttribute("hidden", count === 0);

    const scroll = this.#el("[data-reviews-scroll]");
    if (scroll) {
      scroll.scrollTop = 0;
      requestAnimationFrame(() => this.#updateGradient());
    }
  }

  #matchesFilter(card) {
    // "Alle" toont alleen ≥3★ (bewuste keuze, gelijk aan Elvou);
    // 1★/2★ zijn zichtbaar via het expliciete sterfilter.
    if (this.#activeFilter === "all") return Number.parseInt(card.dataset.stars, 10) >= 3;
    return card.dataset.stars === this.#activeFilter;
  }

  #renderVisibleCards() {
    let visibleIndex = 0;
    for (const card of this.#cards) {
      if (!this.#matchesFilter(card)) {
        card.classList.add(CARD_HIDDEN_CLASS);
        continue;
      }
      visibleIndex++;
      card.classList.toggle(CARD_HIDDEN_CLASS, visibleIndex > this.#visibleCount);
    }
    requestAnimationFrame(() => this.#updateMasonryLayout());
  }

  #updateMasonryLayout() {
    const grid = this.#el("[data-reviews-container]");
    if (!grid) return;

    const visibleCards = this.#cards.filter((card) => !card.classList.contains(CARD_HIDDEN_CLASS));
    if (visibleCards.length === 0) {
      grid.style.height = "0px";
      return;
    }

    const columnCount = this.#columnCount();
    const containerWidth = grid.clientWidth;
    if (containerWidth <= 0) return;

    const columnWidth = (containerWidth - MASONRY_GAP * (columnCount - 1)) / columnCount;
    const columnHeights = Array(columnCount).fill(0);

    for (let index = 0; index < visibleCards.length; index++) {
      const card = visibleCards[index];
      const columnIndex = index % columnCount;
      const x = columnIndex * (columnWidth + MASONRY_GAP);
      const y = columnHeights[columnIndex];

      card.style.position = "absolute";
      card.style.width = `${columnWidth}px`;
      card.style.transform = `translate(${x}px, ${y}px)`;

      const cardHeight = card.getBoundingClientRect().height;
      columnHeights[columnIndex] = y + cardHeight + MASONRY_GAP;
    }

    for (const card of this.#cards) {
      if (!card.classList.contains(CARD_HIDDEN_CLASS)) continue;
      card.style.position = "";
      card.style.width = "";
      card.style.transform = "";
    }

    const maxHeight = Math.max(...columnHeights);
    grid.style.height = `${Math.max(0, maxHeight - MASONRY_GAP)}px`;
  }

  #updateScrollMaxHeight() {
    const scroll = this.#el("[data-reviews-scroll]");
    if (!scroll) return;
    const mobile = Number(this.dataset.scrollMaxMobile) || 500;
    const desktop = Number(this.dataset.scrollMaxDesktop) || 600;
    scroll.style.maxHeight = `${window.matchMedia("(min-width: 768px)").matches ? desktop : mobile}px`;
  }

  #updateGradient() {
    const gradient = this.#el("[data-gradient]");
    const scroll = this.#el("[data-reviews-scroll]");
    if (!gradient || !scroll) return;
    const { scrollTop, scrollHeight, clientHeight } = scroll;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 10;
    gradient.toggleAttribute("hidden", atBottom || scrollHeight <= clientHeight);
  }

  #updateLoadMoreVisibility() {
    const wrapper = this.#el("[data-load-more-wrapper]");
    if (!wrapper) return;
    const hasMore = this.#filteredCards.length > this.#visibleCount;
    wrapper.toggleAttribute("hidden", !hasMore || this.#filteredCards.length === 0);
  }
}

if (!customElements.get("ab-claimsafe-reviews")) {
  customElements.define("ab-claimsafe-reviews", AbClaimsafeReviews);
}
